import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useIsMobile, useItemSelectionController, useMobileDetailRouteFallback, useShowDetailsPane } from '../../hooks'
import Log from '../../services/api/log'
import { useBackfillRecurringTasks, useRecurringTaskTemplates } from '../../services/api/recurring-tasks.hooks'
import { icons } from '../../styles/images'
import SortAndFilterSelectors from '../../utils/sortAndFilter/SortAndFilterSelectors'
import sortAndFilterItems from '../../utils/sortAndFilter/sortAndFilterItems'
import useSortAndFilterSettings from '../../utils/sortAndFilter/useSortAndFilterSettings'
import { TRecurringTaskTemplate } from '../../utils/types'
import { EMPTY_ARRAY } from '../../utils/utils'
import ActionsContainer from '../atoms/ActionsContainer'
import Flex from '../atoms/Flex'
import Spinner from '../atoms/Spinner'
import EmptyDetails from '../details/EmptyDetails'
import TaskDetails from '../details/TaskDetails'
import { Header } from '../molecules/Header'
import AddRecurringTask from '../molecules/recurring-tasks/AddRecurringTask'
import RecurringTask from '../molecules/recurring-tasks/RecurringTask'
import { RECURRING_TASK_SORT_AND_FILTER_CONFIG } from '../molecules/recurring-tasks/recurring-task.config'
import ScrollableListTemplate from '../templates/ScrollableListTemplate'

const RecurringTasksView = () => {
    const { data: recurringTaskTemplates, isLoading: areRecurringTaskTemplatesLoading } = useRecurringTaskTemplates()
    useBackfillRecurringTasks()

    const { recurringTaskId } = useParams()
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    const showDetailsPane = useShowDetailsPane()

    const sortAndFilterSettings = useSortAndFilterSettings<TRecurringTaskTemplate>(
        RECURRING_TASK_SORT_AND_FILTER_CONFIG
    )
    const { selectedFilter, isLoading: areSettingsLoading } = sortAndFilterSettings
    const filteredRecurringTasks = useMemo(() => {
        if (!recurringTaskTemplates || areSettingsLoading) return EMPTY_ARRAY
        return sortAndFilterItems({
            items: recurringTaskTemplates,
            filter: selectedFilter,
            tieBreakerField: RECURRING_TASK_SORT_AND_FILTER_CONFIG.tieBreakerField,
        })
    }, [recurringTaskTemplates, selectedFilter, areSettingsLoading])

    const selectedRecurringTask = useMemo(() => {
        if (filteredRecurringTasks == null || filteredRecurringTasks.length === 0) return null
        return (
            filteredRecurringTasks.find((pr) => pr.id === recurringTaskId) ??
            (isMobile ? null : filteredRecurringTasks[0])
        )
    }, [isMobile, recurringTaskId, filteredRecurringTasks])
    const canValidateRecurringTaskRoute = !areRecurringTaskTemplatesLoading && !areSettingsLoading
    const shouldShowMobileDetailSpinner = useMobileDetailRouteFallback({
        isMobile,
        detailId: recurringTaskId,
        canValidate: canValidateRecurringTaskRoute,
        hasSelectedDetail: Boolean(selectedRecurringTask),
        listPath: '/recurring-tasks',
    })

    useEffect(() => {
        if (isMobile) return
        if (selectedRecurringTask == null) return
        navigate(`/recurring-tasks/${selectedRecurringTask.id}`, { replace: true })
    }, [isMobile, navigate, selectedRecurringTask])

    const selectRecurringTask = useCallback((recurringTask: TRecurringTaskTemplate) => {
        navigate(`/recurring-tasks/${recurringTask.id}`)
        Log(`recurring_task_select_${recurringTask.id}`)
    }, [])

    useItemSelectionController(filteredRecurringTasks ?? EMPTY_ARRAY, selectRecurringTask)

    return (
        <>
            <Flex>
                <ScrollableListTemplate>
                    <Header folderName="Recurring tasks" />
                    {!filteredRecurringTasks ? (
                        <Spinner />
                    ) : (
                        <>
                            <ActionsContainer
                                leftActions={<SortAndFilterSelectors settings={sortAndFilterSettings} />}
                            />
                            <AddRecurringTask />
                            {filteredRecurringTasks.map((recurringTask) => (
                                <RecurringTask
                                    key={recurringTask.id}
                                    recurringTask={recurringTask}
                                    isSelected={recurringTask.id === recurringTaskId}
                                    onSelect={selectRecurringTask}
                                />
                            ))}
                        </>
                    )}
                </ScrollableListTemplate>
            </Flex>
            {showDetailsPane && (!isMobile || recurringTaskId) && (
                <>
                    {selectedRecurringTask ? (
                        <TaskDetails task={selectedRecurringTask} isRecurringTaskTemplate />
                    ) : shouldShowMobileDetailSpinner ? (
                        <Spinner />
                    ) : (
                        <EmptyDetails icon={icons.arrows_repeat} text="You have no recurring tasks" />
                    )}
                </>
            )}
        </>
    )
}

export default RecurringTasksView
